import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  TextField,
} from "@mui/material";
import { Search, ArrowBack } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSystemHealthOverview } from "../../redux/actions";
import dayjs from "dayjs";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export default function ErrorLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [durationFilter, setDurationFilter] = useState("Last 24 Hours");
  const [errorCodeFilter, setErrorCodeFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(getSystemHealthOverview());
  }, [dispatch]);

  const errorLogs =
    useSelector(
      (state) =>
        state.dashboard.getSystemHealthDashboard.data?.details?.error_logs?.data
    ) || [];

  const [sortDirection, setSortDirection] = useState("asc");

  let filteredData = errorLogs.filter((row) => {
    const logTime = dayjs(row.DateTime, "DD/MM/YYYY, hh:mm A");
    const diffInHours = dayjs().diff(logTime, "hour");

    if (durationFilter === "Last 1 Hour") return diffInHours < 1;
    if (durationFilter === "Last 6 Hours") return diffInHours < 6;
    if (durationFilter === "Last 12 Hours") return diffInHours < 12;
    return true;
  });

  if (errorCodeFilter !== "All") {
    filteredData = filteredData.filter(
      (row) => row.ErrorCode.toString() === errorCodeFilter
    );
  }

  if (searchQuery.trim() !== "") {
    filteredData = filteredData.filter((row) =>
      row.APIEndPoint.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  filteredData = filteredData.sort((a, b) => {
    const dateA = dayjs(a.DateTime, "DD/MM/YYYY, hh:mm A");
    const dateB = dayjs(b.DateTime, "DD/MM/YYYY, hh:mm A");
    return sortDirection === "asc"
      ? dateA.isBefore(dateB)
        ? -1
        : 1
      : dateA.isAfter(dateB)
      ? -1
      : 1;
  });

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Select
              value={durationFilter}
              onChange={(e) => setDurationFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "220px",
                height: "40px",
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  color: durationFilter ? "#000" : "#888",
                },
              }}
              renderValue={(selected) => selected || "Duration"}
            >
              <MenuItem>Duration</MenuItem>
              <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
              <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
              <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
              <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
            </Select>

            <Select
              value={errorCodeFilter}
              onChange={(e) => setErrorCodeFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "220px",
                height: "40px",
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  color: errorCodeFilter !== "All" ? "#000" : "#888",
                },
              }}
              renderValue={(selected) =>
                selected !== "All" ? selected : "Error Code"
              }
            >
              <MenuItem value="All">Error Code</MenuItem>
              <MenuItem value="401">401</MenuItem>
              <MenuItem value="403">403</MenuItem>
              <MenuItem value="404">404</MenuItem>
              <MenuItem value="500">500</MenuItem>
            </Select>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Typography
          variant="h6"
          fontWeight={600}
          mb={6}
          sx={{ textAlign: "left" }}
        >
          Error Log Details
        </Typography>

        <Box display="flex" justifyContent="center">
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #ddd",
              width: "100%",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Date & Time
                      </Typography>
                      <Box display="flex" flexDirection="column" sx={{ ml: 1 }}>
                        <Box
                          onClick={() => setSortDirection("asc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            mb: "2px",
                          }}
                        >
                          <ArrowDropUpIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "asc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={() => setSortDirection("desc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        >
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "desc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    API End Point
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Error Code
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Error Message
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Request Method
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    User Agent
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        "& td": {
                          py: 2.5,
                          borderBottom: "1px solid #ddd",
                        },
                      }}
                    >
                      <TableCell>{row.DateTime}</TableCell>
                      <TableCell>{row.APIEndPoint}</TableCell>
                      <TableCell>{row.ErrorCode}</TableCell>
                      <TableCell>{row.ErrorMessage}</TableCell>
                      <TableCell>{row.RequestMethod}</TableCell>
                      <TableCell>{row.UserAgent}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Container>
    </DashboardLayout>
  );
}
